import {useTranslation} from "react-i18next";
import {createSearchParams, generatePath, Link, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../store";
import {selectStore} from "../../../store/selector";
import {
  editTypeAction,
  getLibraryCurrentAction,
  copyTypeAction,
  deleteTypeAction
} from "../../../store/pages/library/actions";
import {
  COMPONENT_TYPES_ENUM,
  FORM_SECTION_TYPES,
  FORM_SECTION_INNER_TYPE,
  FORM_FORM_TYPE, PLANS_TYPES, ROUTE_PATH
} from "../../../constants";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {MyBreadcrumb, MyButton, MyModalForm, MyText} from "../../../components";
import {Card, Col, Collapse, CollapseProps, Divider, Form, Popconfirm, Row} from "antd";
import {StyledCollapse} from "./index.style";
import dayjs from "dayjs";
import {CopyOutlined, DeleteOutlined, EditOutlined} from "@ant-design/icons";
import {getFormAction, getFormActionReset, getFormExtraAction, setFormAction} from "../../../store/common/actions";
import {postJournalLessonAction} from "../../../store/pages/journal/actions";
import {Simulate} from "react-dom/test-utils";
import cancel = Simulate.cancel;

interface IInitialState {
  visible: boolean,
  item?: any,
}

const TYPES = {
  '1': FORM_FORM_TYPE.MATERIALS,
  '2': FORM_FORM_TYPE.TASKS,
  '3': FORM_FORM_TYPE.TESTS,
}

const initialState: IInitialState = {
  visible: false,
  item: {},
}

const modalInitialState = {
  [FORM_SECTION_INNER_TYPE.COPY]: initialState,
  [FORM_SECTION_INNER_TYPE.EDIT]: initialState,
  [FORM_SECTION_INNER_TYPE.DELETE]: initialState,
}

export const LibraryCurrentView = () => {
  const {t} = useTranslation("common")
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formEdit] = Form.useForm();
  const [formCopy] = Form.useForm();
  const [extraFieldsArr, setExtraFieldsArr] = useState([])

  const {subjectId} = useParams()

  const {
    common: {period: periodState, form: formState},
    library: {currentLibrary: currentLibraryState},
  }: RootState = useSelector(selectStore)

  const [formModalData, setFormModalData] = useState(modalInitialState)

  let typeFromSearchParam = searchParams.get("type");
  let captionFromSearchParam = searchParams.get("caption");
  let innerTypeFromSearchParam = searchParams.get("innerType");

  const itemStyle = {
    color: '#1C1C1E',
    fontWeight: '400',
    fontSize: 16
  }

  const itemsArray = useCallback((item: any) => {
    return [
      {
        label: t('label'),
        value: item.name,
        component: (
          <MyText style={itemStyle}>
            {item.name}
          </MyText>
        )
      },
      {
        label: t('public'),
        value: item.is_shared,
        component: (
          <MyText style={itemStyle}>
            {item.is_shared ? t('yes') : t('no')}
          </MyText>
        )
      },
      {
        label: t('content'),
        value: item.content,
        component: (
          <MyText style={itemStyle}>
            {item.content}
          </MyText>
        )
      },
      {
        label: t('videoUrlAddress'),
        value: item.video_url,
        component: (
          <Link to={item.video_url}>
            {item.video_url}
          </Link>
        )
      },
      {
        label: t('source'),
        value: item.source,
        component: (
          <MyText style={itemStyle}>
            {item.source}
          </MyText>
        )
      },
      {
        label: t('urlAddress'),
        value: item.link_url,
        component: (
          <Link to={item.link_url}>
            {item.link_url}
          </Link>
        )
      },
      {
        label: t('textLink'),
        value: item.link_text,
        component: (
          <MyText style={itemStyle}>
            {item.link_text}
          </MyText>
        )
      },
      {
        label: t('broadcastsStartTime'),
        value: item.live_time,
        component: (
          <MyText style={itemStyle}>
            {item.live_time}
          </MyText>
        )
      },
      {
        label: t('broadcastsStartDate'),
        value: item.live_date,
        component: (
          <MyText style={itemStyle}>
            {item.live_date}
          </MyText>
        )
      },
      {
        label: t('welcomeText'),
        value: item.welcome,
        component: (
          <MyText style={itemStyle}>
            {item.welcome}
          </MyText>
        )
      },
      {
        label: t('pdf'),
        value: item.presentation,
        component: (
          item.presentation?.map((file: any, index: any) => (
            <Link to={file} key={index}>
              {file.split('/')[file.split('/').length - 1]}
            </Link>
          )))
      },
      {
        label: t('zoomUrlAddress'),
        value: item.url,
        component: (
          <Link to={item.url}>
            {item.url}
          </Link>
        )
      },
      {
        label: t('file'),
        value: item.file,
        component: (
          Array.isArray(item.file) ? item.file?.map((file: any, index: any) => (
              <Link to={file} key={index}>
                {file?.split('/')[file?.split('/').length - 1]}
              </Link>
            ))
            : <Link to={item.file}>
              {item.file}
            </Link>
        )
      },
      {
        label: t('file'),
        value: item.files,
        component: (
          Array.isArray(item.files) ? item.files?.map((file: any, index: any) => (
              <Link to={file} key={index}>
                {file?.split('/')[file?.split('/').length - 1]}
              </Link>
            ))
            : <Link to={item.files}>
              {item.files}
            </Link>
        )
      },
      {
        label: t('questionCount'),
        value: item.qcount,
        component: (
          <MyText style={itemStyle}>
            {item.qcount}
          </MyText>
        )
      },
      {
        label: t('timeToCompleteOnMinute'),
        value: item.time,
        component: (
          <MyText style={itemStyle}>
            {item.time}
          </MyText>
        )
      }
    ]
  }, [])

  const getCurrentLibrary = () => {
    let body = {
      // @ts-ignore
      type: TYPES[typeFromSearchParam],
      params: {
        subject_id: subjectId,
        ...(innerTypeFromSearchParam === PLANS_TYPES.PUBLIC ? {is_shared: 1} : {}),
      }
    }

    dispatch(getLibraryCurrentAction(body))
  }

  useEffect(() => {
    getCurrentLibrary()
  }, [typeFromSearchParam])

  const contentRender = () => {
    switch (typeFromSearchParam) {
      case '1':
      case '2':
      case '3':
        return (
          <StyledCollapse items={list}/>
        )

      default:
        return false
    }
  }


  const handleClickButton = (type: string, item: any) => {
    switch (type){
      case FORM_SECTION_INNER_TYPE.COPY:
        handleCopy(type, item)
        break
      case FORM_SECTION_INNER_TYPE.EDIT:
        handleEdit(type, item)
        break
      case FORM_SECTION_INNER_TYPE.DELETE:
        handleDelete(item)
        break

      default:
        break
    }

  }

  const handleDelete = (item: any) => {
    const type = {
      '1': 'material',
      '2': 'task',
      '3': 'test',
    }
    dispatch(setFormAction({
      method: 'delete',
      // @ts-ignore
      path: `/sdot/${type[typeFromSearchParam]}/${item.id}`,
      fields: item,
      action: deleteTypeAction
    }))
  }

  const handleCopy = (type: string, item: any) => {
    let currentInnerType = {
      '1': `plan_${type}_material_v1`,
      '2': `plan_${type}_task_v1`,
      '3': `plan_${type}_test_v1`,
    }

    // @ts-ignore
    let api = currentInnerType[typeFromSearchParam]
    let section_type = FORM_SECTION_TYPES.LIBRARY
    // @ts-ignore
    let section_inner_type: any = TYPES[typeFromSearchParam]
    let form_type = type

    dispatch(getFormAction({
      api: api,
      section_type: section_type,
      section_inner_type: section_inner_type,
      form_type: form_type,

      extra: {
        field_name: 'to_ctp_id',
        extra_value: subjectId
      }
    }))

    setFormModalData(prevState => ({
      ...prevState,
      [type]: {
        ...prevState[type],
        visible: true,
        item,
        form: {
          section_type: section_type,
          section_inner_type: section_inner_type,
          form_type: form_type
        }
      }
    }))
  }

  const handleEdit = (type: string, item: any) => {
    let api = item?.formEdit
    let section_type = FORM_SECTION_TYPES.LIBRARY
    // @ts-ignore
    let section_inner_type: any = TYPES[typeFromSearchParam]
    let form_type = type


    dispatch(getFormAction({
      api: api,
      section_type: section_type,
      section_inner_type: section_inner_type,
      form_type: form_type,
    }))

    setFormModalData(prevState => ({
      ...prevState,
      [type]: {
        ...prevState[type],
        visible: true,
        item,
        form: {
          section_type: section_type,
          section_inner_type: section_inner_type,
          form_type: form_type
        }
      }
    }))
  }

  const handleFormModalClose = () => {
    setFormModalData(modalInitialState)
    dispatch(getFormActionReset())
    formEdit.resetFields()
    formCopy.resetFields()
    setExtraFieldsArr([])
  }

  const handleModalCopyFormSave = (values: any) => {
    const state: IInitialState = formModalData[FORM_SECTION_INNER_TYPE.COPY]
    // @ts-ignore
    const main: any = formState[FORM_SECTION_TYPES.LIBRARY][TYPES[typeFromSearchParam]][FORM_SECTION_INNER_TYPE.COPY].data[0]

    let type = {
      '1': 'material_id',
      '2': 'task_id',
      '3': 'test_id',
    }

    let body = {
      // @ts-ignore
      [type[typeFromSearchParam]]: state?.item?.id,
      ...values,
    }

    dispatch(setFormAction({
      method: main?.method,
      path: main?.action,
      fields: body,
      action: copyTypeAction
    }))

    handleFormModalClose()
  }

  const handleModalEditFormSave = (values: any) => {
    const state: IInitialState = formModalData[FORM_SECTION_INNER_TYPE.EDIT]
    // @ts-ignore
    const main: any = formState[FORM_SECTION_TYPES.LIBRARY][TYPES[typeFromSearchParam]][FORM_SECTION_INNER_TYPE.EDIT].data[0]

    let body = {
      material_id: state?.item?.id,
      ...values,
    }

    dispatch(setFormAction({
      method: main?.method,
      path: `${main?.action}/${state?.item?.id}`,
      fields: body,
      action: editTypeAction
    }))

    handleFormModalClose()
  }


  const modalCopyChangeValues = (changedFields: any, allFields: any) => {
    // @ts-ignore
    const fieldsArr = formState[FORM_SECTION_TYPES.LIBRARY]?.[TYPES[typeFromSearchParam]]?.[FORM_SECTION_INNER_TYPE.COPY].data[0]
    let state: any = formModalData[FORM_SECTION_INNER_TYPE.COPY]

    let fieldName = Object.keys(changedFields)[0]
    let fieldValue = Object.values(changedFields)[0]

    let getObject = fieldsArr?.fields.find((fieldItem: any) => fieldItem.name === fieldName)
    let getExtraField = getObject?.fieldValues?.find((fieldItem: any) => fieldItem.value === fieldValue)

    //todo ДЕнис напомнит
    if(fieldName === 'to_ctp_id'){
      let body = {
        data: [fieldsArr],
        api: null,
        section_type: state?.form?.section_type,
        section_inner_type: state?.form?.section_inner_type,
        form_type: state?.form?.form_type,

        extra: {
          field_name: 'to_lesson_id',
          extra_value: fieldValue
        }
      }

      dispatch(getFormExtraAction(body))
    }
  }

  const modalEditChangeValues = (changedFields: any, allFields: any) => {
    // @ts-ignore
    const fieldsArr = formState[FORM_SECTION_TYPES.LIBRARY]?.[TYPES[typeFromSearchParam]]?.[FORM_SECTION_INNER_TYPE.EDIT].data[0]?.fields
    let fieldName = Object.keys(changedFields)[0]
    let fieldValue = Object.values(changedFields)[0]
    let getObject = fieldsArr.find((fieldItem: any) => fieldItem.name === fieldName)
    let getExtraField = getObject?.fieldValues?.find((fieldItem: any) => fieldItem.value === fieldValue)

    switch (getObject?.type) {
      case COMPONENT_TYPES_ENUM.TYPE_SELECT:
        if (getExtraField?.fields){
          setExtraFieldsArr(getExtraField?.fields)
        } else {
          setExtraFieldsArr([])
        }
        break

      default:
        break
    }
  }

  const handleNavigate = (url: any, id: any) => {
    const path = {
      // @ts-ignore
      pathname: generatePath(url, {subjectId: subjectId, testId: id}),
      // @ts-ignore
      search: createSearchParams({
        type: typeFromSearchParam,
        caption: captionFromSearchParam,
        innerType: innerTypeFromSearchParam,
        from: ROUTE_PATH.LIBRARY
      }).toString(),
    }

    navigate(path, {state: id});
  }

  const list: CollapseProps['items'] = useMemo(() => {
    return currentLibraryState.data?.map((item: any) => ({
      key: item?.id,
      label: (
        <Row align='middle' justify='space-between'>
          <Col>
            <MyText>
              {item?.name}
            </MyText>
          </Col>

          <Col>
            <MyText>
              {item?.updated_at && dayjs(item?.updated_at).format('DD.MM.YYYY HH:mm')}
            </MyText>
          </Col>
        </Row>
      ),
      children: (
        <Row align='middle' justify='center'>
          <Col span={24}>
            <Row align='middle' justify='center'>
              {
                itemsArray(item)?.filter((inner: any) => inner.value)?.map((inner: any, index: any) => (
                  <Col key={index} span={24} className='item'>
                    <Row>
                      <Col span={24}>
                        <MyText size={14} weight={400} color={'#8C8C8C'}>
                          {inner.label}
                        </MyText>
                      </Col>

                      <Col span={24}>
                        {inner.component}
                      </Col>
                    </Row>
                  </Col>
                ))
              }
            </Row>
          </Col>

          <Col span={24} className='item'>
            <Row gutter={[16, 16]}>
              {/*{*/}
              {/*  innerTypeFromSearchParam === PLANS_TYPES.PRIVATE &&*/}
              {/*  <Col>*/}
              {/*    <MyButton title='Редактировать' warning icon={<EditOutlined/>}*/}
              {/*              onClick={() => handleClickButton(FORM_SECTION_INNER_TYPE.EDIT, item)}/>*/}
              {/*  </Col>*/}
              {/*}*/}

              {/*<Col>*/}
              {/*  <MyButton title='Копировать' info icon={<CopyOutlined/>}*/}
              {/*            onClick={() => handleClickButton(FORM_TYPES.COPY, item)}/>*/}
              {/*</Col>*/}

              {
                innerTypeFromSearchParam === PLANS_TYPES.PRIVATE &&
                <Col>
                  <Popconfirm
                    placement="bottomRight"
                    title={t('delete')}
                    description={'Вы уверены, что хотите удалить?'}
                    onConfirm={() => handleClickButton(FORM_SECTION_INNER_TYPE.DELETE, item)}
                    okText="Да"
                    cancelText="Нет"
                  >
                    <MyButton title='Удалить' danger icon={<DeleteOutlined/>}/>
                  </Popconfirm>
                </Col>
              }

              {
                // @ts-ignore
                typeFromSearchParam == 3 &&

                <Col>
                  <Row gutter={[16, 16]}>
                    {/*<Col>*/}
                    {/*  <MyButton title='Редактировать тесты вопрос-ответ' type='primary' ghost onClick={() => handleNavigate(ROUTE_PATH.LIBRARY_CURRENT_TEST, item.id)}/>*/}
                    {/*</Col>*/}

                    <Col>
                      <MyButton title='Просмотр вопросов' type='primary' ghost onClick={() => handleNavigate(ROUTE_PATH.LIBRARY_CURRENT_TEST_VIEW, item.id)}/>
                    </Col>
                  </Row>
                </Col>
              }
            </Row>
          </Col>
        </Row>
      )
    }))
  }, [currentLibraryState.data])

  return (
    <>
      <MyBreadcrumb title={t('back')} isBack/>

      <Row justify='center'>
        <Col span={20}>
          <Row>
            <Col span={24}>
              <Card
                loading={currentLibraryState.loading}
              >
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <MyText size={24} weight={500}>
                      {captionFromSearchParam}
                    </MyText>
                  </Col>

                  <Col span={24}>
                    {contentRender()}
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      <MyModalForm
        formName={FORM_SECTION_INNER_TYPE.COPY}
        form={formCopy}
        // @ts-ignore
        title={formState[FORM_SECTION_TYPES.LIBRARY]?.[TYPES[typeFromSearchParam]]?.[FORM_SECTION_INNER_TYPE.COPY]?.data[0]?.title}
        visible={formModalData[FORM_SECTION_INNER_TYPE.COPY]?.visible}
        // @ts-ignore
        state={formState[FORM_SECTION_TYPES.LIBRARY]?.[TYPES[typeFromSearchParam]]?.[FORM_SECTION_INNER_TYPE.COPY]}
        onCancel={handleFormModalClose}
        onFinish={handleModalCopyFormSave}
        period={periodState.data?.[0]}
        onValuesChange={(changedFields: any, allFields: any) => modalCopyChangeValues(changedFields, allFields)}
      />

      <MyModalForm
        formName={FORM_SECTION_INNER_TYPE.EDIT}
        form={formEdit}
        // @ts-ignore
        title={formState[FORM_SECTION_TYPES.LIBRARY]?.[TYPES[typeFromSearchParam]]?.[FORM_SECTION_INNER_TYPE.EDIT]?.data[0]?.title}
        visible={formModalData[FORM_SECTION_INNER_TYPE.EDIT]?.visible}
        // @ts-ignore
        state={formState[FORM_SECTION_TYPES.LIBRARY]?.[TYPES[typeFromSearchParam]]?.[FORM_SECTION_INNER_TYPE.EDIT]}
        onCancel={handleFormModalClose}
        onFinish={handleModalEditFormSave}
        period={periodState.data?.[0]}
        onValuesChange={(changedFields: any, allFields: any) => modalEditChangeValues(changedFields, allFields)}
        extraFieldsArr={extraFieldsArr}
      />
    </>
  )
}
